import { get, find, isEmpty, isBoolean, intersection } from 'lodash';

import { serializeManagerName } from './general';
import { serializeChainType } from './chain';
import { getCallURLById } from '../../utils/parsers';

import { CLIENT_LOG_TYPES, TICKET_LOG_TYPES } from '../../consts/logs';
import { addMinutes, fromUnixTime, getUnixTime } from 'date-fns';

const SynteticTypes = {
  BOT_EMAIL: 'manager_email',
};

export const serializeLogAttachments = attachments =>
  attachments.map(attach => ({
    id: String(attach.id),
    name: attach.name,
    size: attach.size,
    url: attach.url,
  }));

const isSerializableObject = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const setIfHas = (target, source, sourceKey, targetKey) => {
  if (hasOwn(source, sourceKey)) {
    target[targetKey] = source[sourceKey];
  }
};

const serializeFinalCallSummaryEvidence = evidence => {
  if (!isSerializableObject(evidence)) {
    return null;
  }

  const parsedEvidence = {};
  setIfHas(parsedEvidence, evidence, 'source_type', 'sourceType');
  setIfHas(parsedEvidence, evidence, 'source_id', 'sourceId');
  setIfHas(parsedEvidence, evidence, 'quote', 'quote');

  return parsedEvidence;
};

const serializeCallSummary = summary => {
  if (!isSerializableObject(summary)) {
    return null;
  }

  const parsedSummary = {};
  setIfHas(parsedSummary, summary, 'client_reason', 'clientReason');
  setIfHas(
    parsedSummary,
    summary,
    'conversation_outcome',
    'conversationOutcome'
  );
  setIfHas(parsedSummary, summary, 'what_we_did_right', 'whatWeDidRight');
  setIfHas(parsedSummary, summary, 'what_we_did_wrong', 'whatWeDidWrong');
  setIfHas(parsedSummary, summary, 'next_best_action', 'nextBestAction');
  setIfHas(parsedSummary, summary, 'sentiment', 'sentiment');
  setIfHas(
    parsedSummary,
    summary,
    'service_dissatisfaction_score',
    'serviceDissatisfactionScore'
  );
  setIfHas(
    parsedSummary,
    summary,
    'service_dissatisfaction_label',
    'serviceDissatisfactionLabel'
  );
  setIfHas(
    parsedSummary,
    summary,
    'service_dissatisfaction_reason',
    'serviceDissatisfactionReason'
  );
  setIfHas(
    parsedSummary,
    summary,
    'outcome_dissatisfaction_score',
    'outcomeDissatisfactionScore'
  );
  setIfHas(
    parsedSummary,
    summary,
    'outcome_dissatisfaction_label',
    'outcomeDissatisfactionLabel'
  );
  setIfHas(
    parsedSummary,
    summary,
    'outcome_dissatisfaction_reason',
    'outcomeDissatisfactionReason'
  );

  if (hasOwn(summary, 'evidence')) {
    parsedSummary.evidence = Array.isArray(summary.evidence)
      ? summary.evidence.map(serializeFinalCallSummaryEvidence).filter(Boolean)
      : [];
  }

  setIfHas(parsedSummary, summary, 'confidence', 'confidence');

  return parsedSummary;
};

export const serializeFinalCallSummary = finalCallSummary => {
  if (!isSerializableObject(finalCallSummary)) {
    return null;
  }

  const parsedFinalCallSummary = {};
  setIfHas(parsedFinalCallSummary, finalCallSummary, 'status', 'status');
  setIfHas(
    parsedFinalCallSummary,
    finalCallSummary,
    'generated_at',
    'generatedAt'
  );

  if (hasOwn(finalCallSummary, 'summary')) {
    parsedFinalCallSummary.summary = serializeCallSummary(
      finalCallSummary.summary
    );
  }

  setIfHas(parsedFinalCallSummary, finalCallSummary, 'llm_model', 'llmModel');
  setIfHas(
    parsedFinalCallSummary,
    finalCallSummary,
    'prompt_version',
    'promptVersion'
  );

  return parsedFinalCallSummary;
};

export const serializeClientLogs = ({
  logs: unparsedData,
  reasons: allReasons,
  requireNoteIds,
  segments: allSegments,
  currentManager,
}) => {
  const {
    call_reasons: unparsedReasons,
    calls: unparsedCalls,
    communication_logs: unparsedLogs,
    notes: unparsedNotes,
    mails: unparsedMails,
    persons: unparsedPersons,
    manager_emails: unparsedBotEmails,
    messages: unparsedMessages,
  } = unparsedData;
  const types = new Set();

  if (isEmpty(unparsedLogs)) {
    return {
      logs: [],
      types: [],
    };
  }

  const logTypes = Object.values({ ...CLIENT_LOG_TYPES, ...SynteticTypes });
  const logs = unparsedLogs.map(unparsedLog => {
    const logId = get(unparsedLog, 'id');

    const parsedLog = {
      active: false,
      id: logId ? String(logId) : null,
    };

    const unparsedContactId = get(unparsedLog, 'person_id');
    const contactId = unparsedContactId ? String(unparsedContactId) : null;
    const unparsedContact = find(unparsedPersons, { person_id: +contactId });

    const unparsedManagerId = get(unparsedLog, 'manager_id');
    const managerId = unparsedManagerId ? String(unparsedManagerId) : null;
    const unparsedManager = find(unparsedPersons, {
      person_id: +managerId,
      person_type: 'manager',
    });
    const unparsedManagerFullName = get(unparsedManager, 'full_name');

    const isSupervisor = get(currentManager, 'isSupervisor');
    const currentManagerId = get(currentManager, 'id');
    const isAuthor = currentManagerId === managerId;
    const editable = isAuthor || isSupervisor;

    const logKind = get(unparsedLog, 'kind');
    const type = find(logTypes, logType => logType.toLowerCase() === logKind);
    parsedLog.type = type || null;

    parsedLog.ticketId = get(unparsedLog, 'ticket_id');
    parsedLog.ticketType = get(unparsedLog, 'ticket_kind');

    const communicationId = get(unparsedLog, 'communication_id');
    switch (type) {
      case CLIENT_LOG_TYPES.CALL: {
        const unparsedCall = find(unparsedCalls, { id: communicationId });
        const unparsedIsIncoming = get(unparsedCall, 'is_incoming');
        const isIncoming = isBoolean(unparsedIsIncoming)
          ? unparsedIsIncoming
          : null;
        const path = isIncoming ? 'incoming' : 'outgoing';
        const reasonGroups = get(allReasons, `${path}.groups`) || [];
        const reasons = get(allReasons, `${path}.reasons`) || [];
        const isSupervisorInCall =
          get(unparsedManager, 'sip_supervisor') ||
          get(unparsedContact, 'sip_supervisor');

        parsedLog.time = get(unparsedCall, 'started_at') || null;
        parsedLog.call = {
          answeredAt: get(unparsedCall, 'answered_at') || null,
          callUrl: getCallURLById(get(unparsedCall, 'id')),
          editable,
          finalCallSummary: serializeFinalCallSummary(
            get(unparsedCall, 'final_call_summary')
          ),
          finishedAt: get(unparsedCall, 'finished_at') || null,
          id: communicationId ? String(communicationId) : null,
          isAnswered: get(unparsedCall, 'is_answered'),
          isCallback: get(unparsedCall, 'is_callback') || false,
          isIncoming,
          reasonDescription: '',
          reasonGroups,
          reasons,
          recordUrl: !isSupervisorInCall && get(unparsedCall, 'record_url'),
          requireNoteIds: requireNoteIds.map(String),
          segments: allSegments,
          selectedReasonIds: [],
          selectedSegmentId: get(unparsedCall, 'call_segment_id') || null,
        };

        parsedLog.name = serializeManagerName(
          'call',
          managerId,
          unparsedManagerFullName,
          isIncoming
        );

        const selectedReasonIds = get(unparsedCall, 'call_reason_ids') || [];
        if (!isEmpty(selectedReasonIds)) {
          const reasonIds = [];
          selectedReasonIds.forEach(id => {
            const unparsedReason = find(unparsedReasons, { id });
            const reasonId = get(unparsedReason, 'reason_id');
            const segmentId = get(unparsedReason, 'segment_id');

            if (reasonId) {
              reasonIds.push(String(reasonId));
            }

            if (segmentId) {
              parsedLog.call.selectedSegmentId = String(segmentId);
            }
          });
          parsedLog.call.selectedReasonIds = reasonIds;

          const intersectionItem = intersection(
            parsedLog.call.requireNoteIds,
            reasonIds
          );
          if (!isEmpty(intersectionItem)) {
            const unparsedReasonWithDescription = find(
              unparsedReasons,
              r => r.reason_id === intersectionItem[0]
            );
            parsedLog.call.reasonDescription =
              get(unparsedReasonWithDescription, 'description') || '';
          }
        }

        types.add(CLIENT_LOG_TYPES.CALL);

        break;
      }

      case CLIENT_LOG_TYPES.NOTE: {
        parsedLog.name = serializeManagerName(
          'note',
          managerId,
          unparsedManagerFullName
        );

        const unparsedNote = find(unparsedNotes, { id: communicationId });

        parsedLog.time = get(unparsedNote, 'created_at') || null;
        parsedLog.note = {
          editable,
          editableExpiresAt:
            !isSupervisor &&
            getUnixTime(addMinutes(fromUnixTime(parsedLog.time), 15)),
          id: communicationId,
          isPinned: get(unparsedNote, 'is_pinned') || false,
          text: get(unparsedNote, 'body'),
          attachments: serializeLogAttachments(
            get(unparsedNote, 'attachments', [])
          ),
        };

        types.add(CLIENT_LOG_TYPES.NOTE);

        break;
      }

      case CLIENT_LOG_TYPES.MESSAGE: {
        const unparsedMessage = find(unparsedMessages, { id: communicationId });

        parsedLog.time = get(unparsedMessage, 'created_at') || null;
        parsedLog.text = get(unparsedMessage, 'content') || null;

        break;
      }

      case CLIENT_LOG_TYPES.EMAIL: {
        parsedLog.name = serializeManagerName(
          'email',
          managerId,
          unparsedManagerFullName
        );

        const unparsedMail = find(unparsedMails, { id: communicationId });

        parsedLog.time = get(unparsedMail, 'created_at') || null;
        parsedLog.read = get(unparsedMail, 'is_read');
        parsedLog.text = get(unparsedMail, 'subject');

        parsedLog.email = {
          id: get(unparsedMail, 'chain_id'),
        };

        types.add(CLIENT_LOG_TYPES.EMAIL);

        break;
      }

      case SynteticTypes.BOT_EMAIL: {
        const unparsedMail = find(unparsedBotEmails, { id: communicationId });

        parsedLog.time = get(unparsedMail, 'created_at') || null;
        parsedLog.read = get(unparsedMail, 'is_read');
        parsedLog.text = get(unparsedMail, 'subject');

        parsedLog.type = CLIENT_LOG_TYPES.EMAIL;
        parsedLog.email = {
          id: get(unparsedMail, 'id'),
          isBot: true,
        };

        types.add(CLIENT_LOG_TYPES.MANAGER_EMAIL);

        break;
      }

      case CLIENT_LOG_TYPES.MESSAGE_GROUP: {
        const messengerType = get(unparsedLog, 'transport');
        const isOutgoingMessage = !get(unparsedLog, 'is_incoming');

        parsedLog.time = get(unparsedLog, 'created_at') || null;

        if (isOutgoingMessage) {
          parsedLog.name = serializeManagerName(
            'email',
            managerId,
            unparsedManagerFullName
          );
        }

        parsedLog.group = {
          messenger: serializeChainType(messengerType),
          count: get(unparsedLog, 'messages_count'),
        };

        types.add(CLIENT_LOG_TYPES.MESSAGE);

        break;
      }

      default: {
        break;
      }
    }

    return parsedLog;
  });

  return {
    logs,
    types: Array.from(types),
  };
};

export const extendTicketLogWithPermissions = ({
  ticketLog,
  currentManager,
}) => {
  const { id: currentManagerId, isSupervisor } = currentManager;
  const isAuthor = currentManagerId === ticketLog.managerId;
  const createdAt = ticketLog.time;

  return {
    ...ticketLog,
    editable: isAuthor || isSupervisor,
    editableExpiresAt: !isSupervisor
      ? getUnixTime(addMinutes(fromUnixTime(createdAt), 15))
      : null,
  };
};

export const serializeTicketLog = unparsedLog => {
  const {
    id: logId,
    ticket_id: ticketId,
    whodunnit_id: managerId,
    grouped,
  } = unparsedLog;

  const managerInfo = {
    avatarUrl: get(unparsedLog, 'whodunnit_avatar_url'),
    managerId: managerId ? String(managerId) : null,
    name: get(unparsedLog, 'whodunnit_name'),
  };

  if (grouped) {
    const items = get(unparsedLog, 'group_items', []);

    return {
      ...managerInfo,
      grouped,
      groupId: `group-${managerId}-${items[0]?.id}`,
      isExpanded: false,
      groupCount: get(unparsedLog, 'group_count'),
      groupItems: items.map(serializeTicketLog),
    };
  }

  return {
    ...managerInfo,
    id: logId ? String(logId) : null,
    ticketId: ticketId ? String(ticketId) : null,
    message: get(unparsedLog, 'message'),
    isPinned: get(unparsedLog, 'is_pinned'),
    time: get(unparsedLog, 'created_at'),
    type: get(unparsedLog, 'kind'),
    value: get(unparsedLog, 'value'),
    valueText: get(unparsedLog, 'value_text'),
    editableExpiresAt: null,
    attachments: get(unparsedLog, 'attachments')
  };
};

export const serializeTicketLogs = unparsedLogs => {
  if (isEmpty(unparsedLogs)) {
    return {
      logs: [],
      types: [],
    };
  }

  const types = new Set();
  const logs = unparsedLogs.map(unparsedLog => {
    if (unparsedLog.grouped) {
      get(unparsedLog, 'group_items', []).map(log => {
        types.add(get(log, 'kind'));
        return serializeTicketLog(log);
      });
    } else {
      types.add(get(unparsedLog, 'kind'));
    }
    return serializeTicketLog(unparsedLog);
  });

  return {
    logs,
    types: Array.from(types),
  };
};

export const serializeTicketLogsMeta = meta => ({
  page: meta.page,
  size: meta.size,
  total: meta.total,
  totalPages: meta.total_pages,
});
